import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ArrowRight, ArrowLeft, ZapOff, Zap, Check, AlertCircle, Loader2, Coins, X } from 'lucide-react';
import { StepIndicator } from '@/components/StepIndicator';
import { UploadedImage, GenerationResult, GenerationType, RoomType, UpscaleResult } from '@/types';
import { getRoomLabel } from '@/lib/roomConfig';
import { formatCredits } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// ============================================================================
// Types
// ============================================================================

interface UpscaleSelection {
  imageId: string;
  variant: 'staged' | 'empty';
  storagePath: string;
  sourceUrl: string;
}

interface UpscaleSelectionViewProps {
  uploadedImages: UploadedImage[];
  results: GenerationResult[];
  credits: number;
  upscaleResults: Map<string, UpscaleResult>;
  isUpscaling: boolean;
  onUpscaleSelected: (selections: UpscaleSelection[]) => Promise<void>;
  onSkip: () => void;
  onBack?: () => void;
  onBuyCredits: () => void;
  onNext: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const CREDIT_COST_PER_IMAGE = 0.5;

// ============================================================================
// Before/After Comparison Slider
// ============================================================================

interface ComparisonSliderProps {
  beforeUrl: string;
  afterUrl: string;
  label: string;
  onClose: () => void;
}

const ComparisonSlider: React.FC<ComparisonSliderProps> = ({ beforeUrl, afterUrl, label, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(pct);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      updatePosition(e.clientX);
    };
    const handleTouchMove = (e: TouchEvent) => {
      updatePosition(e.touches[0].clientX);
    };
    const handleEnd = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, updatePosition]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-5xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors z-10"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Title */}
        <div className="absolute -top-12 left-0 text-white/90 text-sm font-medium">
          {label} — Vorher / Nachher
        </div>

        {/* Comparison container */}
        <div
          ref={containerRef}
          className="relative w-full overflow-hidden rounded-lg select-none cursor-col-resize"
          onMouseDown={(e) => { setIsDragging(true); updatePosition(e.clientX); }}
          onTouchStart={(e) => { setIsDragging(true); updatePosition(e.touches[0].clientX); }}
        >
          {/* After image (full width, bottom layer) */}
          <img
            src={afterUrl}
            alt="Nachher (hochskaliert)"
            className="w-full block"
            draggable={false}
          />

          {/* Before image (clipped via clip-path so it stays full-width) */}
          <img
            src={beforeUrl}
            alt="Vorher (original)"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            draggable={false}
          />

          {/* Slider line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
            style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          >
            {/* Slider handle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
              <div className="flex items-center gap-0.5 text-gray-700">
                <ArrowLeft className="w-3 h-3" />
                <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </div>

          {/* Labels */}
          <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
            Vorher
          </div>
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
            Nachher (4K)
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const UpscaleSelectionView: React.FC<UpscaleSelectionViewProps> = ({
  uploadedImages,
  results,
  credits,
  upscaleResults,
  isUpscaling,
  onUpscaleSelected,
  onSkip,
  onBack,
  onBuyCredits,
  onNext,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [previewItem, setPreviewItem] = useState<{ beforeUrl: string; afterUrl: string; label: string } | null>(null);

  // Build the list of upscalable images from results
  const upscalableImages = useMemo(() => {
    const items: Array<{
      key: string;
      imageId: string;
      variant: 'staged' | 'empty';
      storagePath: string;
      sourceUrl: string;
      roomLabel: string;
    }> = [];

    for (const img of uploadedImages) {
      const imgResults = results.filter((r) => r.originalImageId === img.id);

      for (const res of imgResults) {
        if (!res.currentUrl || !res.storagePath || !res.dbImageId) continue;

        const variant: 'staged' | 'empty' = res.type === GenerationType.EMPTY ? 'empty' : 'staged';
        const key = `${res.dbImageId}_${variant}`;

        const roomLabel =
          img.roomType === RoomType.CUSTOM && img.customLabel
            ? img.customLabel
            : getRoomLabel(img.roomType as string);

        items.push({
          key,
          imageId: res.dbImageId,
          variant,
          storagePath: res.storagePath,
          sourceUrl: res.currentUrl,
          roomLabel,
        });
      }
    }

    return items;
  }, [uploadedImages, results]);

  // Calculate cost
  const selectedCount = selectedKeys.size;
  const totalCost = selectedCount * CREDIT_COST_PER_IMAGE;
  const hasEnoughCredits = credits >= totalCost;

  // Check if all upscaling is done (for showing the "Next" button)
  const allUpscalingDone = useMemo(() => {
    if (upscaleResults.size === 0) return false;
    for (const [, result] of upscaleResults) {
      if (result.isUpscaling) return false;
    }
    return true;
  }, [upscaleResults]);

  const hasAnyErrors = useMemo(() => {
    for (const [, result] of upscaleResults) {
      if (result.error) return true;
    }
    return false;
  }, [upscaleResults]);

  // Toggle selection
  const toggleSelection = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Select all / deselect all
  const toggleSelectAll = useCallback(() => {
    if (selectedKeys.size === upscalableImages.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(upscalableImages.map((item) => item.key)));
    }
  }, [selectedKeys.size, upscalableImages]);

  // Handle upscale action
  const handleUpscale = useCallback(async () => {
    const selections: UpscaleSelection[] = upscalableImages
      .filter((item) => selectedKeys.has(item.key))
      .map((item) => ({
        imageId: item.imageId,
        variant: item.variant,
        storagePath: item.storagePath,
        sourceUrl: item.sourceUrl,
      }));

    if (selections.length > 0) {
      await onUpscaleSelected(selections);
    }
  }, [selectedKeys, upscalableImages, onUpscaleSelected]);

  // Handle image click for preview
  const handleImageClick = useCallback((item: typeof upscalableImages[0]) => {
    const upscaleResult = upscaleResults.get(item.key);
    if (upscaleResult?.upscaledUrl) {
      setPreviewItem({
        beforeUrl: item.sourceUrl,
        afterUrl: upscaleResult.upscaledUrl,
        label: `${item.roomLabel} — ${item.variant === 'staged' ? 'Eingerichtet' : 'Leer'}`,
      });
    }
  }, [upscaleResults]);

  return (
    <div className="space-y-8 animate-fade-in">
      <StepIndicator currentStep="upscale" onBack={onBack} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Upscaling</h2>
          <p className="text-muted-foreground">
            Bilder auf 4K hochskalieren (optional). 0,5 Credits pro Bild.
          </p>
        </div>
        <div className="flex gap-3">
          {onBack && !isUpscaling && (
            <Button variant="ghost" onClick={onBack} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Zurück
            </Button>
          )}
          {allUpscalingDone ? (
            <Button onClick={onNext} className="flex items-center gap-2">
              Weiter: Branding Studio <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={isUpscaling}
              className="flex items-center gap-2"
            >
              <ZapOff className="w-4 h-4" /> Überspringen
            </Button>
          )}
        </div>
      </div>

      {/* Credit Summary Bar */}
      {!isUpscaling && !allUpscalingDone && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-6 text-sm">
                <span className="text-muted-foreground">
                  Ausgewählt: <span className="font-semibold text-foreground">{selectedCount} Bilder</span>
                </span>
                <span className="text-muted-foreground">
                  Kosten: <span className="font-semibold text-foreground">{totalCost} Credits</span>
                </span>
                <span className={`flex items-center gap-1 ${hasEnoughCredits ? 'text-muted-foreground' : 'text-destructive'}`}>
                  <Coins className="w-4 h-4" />
                  Guthaben: <span className="font-semibold">{formatCredits(credits)} Credits</span>
                </span>
              </div>
              <div className="flex gap-3">
                {!hasEnoughCredits && selectedCount > 0 && (
                  <Button variant="outline" size="sm" onClick={onBuyCredits}>
                    Guthaben aufladen
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAll}
                >
                  {selectedKeys.size === upscalableImages.length ? 'Keine auswählen' : 'Alle auswählen'}
                </Button>
                <Button
                  onClick={handleUpscale}
                  disabled={selectedCount === 0 || !hasEnoughCredits}
                  className="flex items-center gap-2"
                >
                  <Zap className="w-4 h-4" />
                  Ausgewählte hochskalieren
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Grid */}
      {upscalableImages.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Keine generierten Bilder zum Hochskalieren vorhanden.</p>
            <Button variant="outline" onClick={onSkip} className="mt-4">
              Weiter zum Branding
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {upscalableImages.map((item) => {
            const isSelected = selectedKeys.has(item.key);
            const upscaleResult = upscaleResults.get(item.key);
            const isItemUpscaling = upscaleResult?.isUpscaling ?? false;
            const isItemDone = upscaleResult?.upscaledUrl != null;
            const itemError = upscaleResult?.error;

            return (
              <Card
                key={item.key}
                className={`overflow-hidden cursor-pointer transition-all duration-200 ${
                  isSelected && !isUpscaling
                    ? 'ring-2 ring-primary shadow-lg'
                    : isItemDone
                      ? 'ring-2 ring-green-500'
                      : itemError
                        ? 'ring-2 ring-destructive'
                        : 'hover:shadow-md'
                }`}
                onClick={() => {
                  if (isItemDone) {
                    handleImageClick(item);
                  } else if (!isUpscaling && !itemError) {
                    toggleSelection(item.key);
                  }
                }}
              >
                {/* Image */}
                <div className="relative aspect-[4/3] bg-muted">
                  <img
                    src={isItemDone ? upscaleResult!.upscaledUrl! : item.sourceUrl}
                    alt={item.roomLabel}
                    className="w-full h-full object-cover"
                  />

                  {/* Selection checkbox (top-left) */}
                  {!isUpscaling && !isItemDone && !itemError && (
                    <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'bg-background/80 border-muted-foreground/40'
                    }`}>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  )}

                  {/* Status overlay */}
                  {isItemUpscaling && (
                    <div className="absolute inset-0 bg-background/60 flex flex-col items-center justify-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <span className="text-sm font-medium text-foreground">Wird hochskaliert...</span>
                    </div>
                  )}

                  {isItemDone && (
                    <div className="absolute top-2 right-2">
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  )}

                  {itemError && (
                    <div className="absolute inset-0 bg-destructive/10 flex flex-col items-center justify-center gap-2 p-4">
                      <AlertCircle className="w-8 h-8 text-destructive" />
                      <span className="text-xs text-destructive text-center">{itemError}</span>
                    </div>
                  )}

                  {/* Variant badge (bottom-right) */}
                  <div className="absolute bottom-2 right-2">
                    <Badge
                      variant="secondary"
                      className={
                        item.variant === 'staged'
                          ? 'bg-green-500 text-white hover:bg-green-600 border-transparent'
                          : 'bg-blue-500 text-white hover:bg-blue-600 border-transparent'
                      }
                    >
                      {item.variant === 'staged' ? 'Eingerichtet' : 'Leer'}
                    </Badge>
                  </div>
                </div>

                {/* Label */}
                <CardContent className="p-3">
                  <p className="text-sm font-medium text-foreground truncate">{item.roomLabel}</p>
                  {isItemDone && (
                    <p className="text-xs text-green-600 mt-1">4K hochskaliert — Klicken für Vergleich</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Bottom action bar when upscaling is done */}
      {allUpscalingDone && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-foreground font-medium">
                  {upscaleResults.size} {upscaleResults.size === 1 ? 'Bild' : 'Bilder'} erfolgreich hochskaliert
                  {hasAnyErrors && ' (einige fehlgeschlagen)'}
                </span>
              </div>
              <Button onClick={onNext} className="flex items-center gap-2">
                Weiter: Branding Studio <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Before/After Preview Modal */}
      {previewItem && (
        <ComparisonSlider
          beforeUrl={previewItem.beforeUrl}
          afterUrl={previewItem.afterUrl}
          label={previewItem.label}
          onClose={() => setPreviewItem(null)}
        />
      )}
    </div>
  );
};
